import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (player: { email: string; points: number }) => void;
}

export const AddPlayerRankingModal: React.FC<Props> = ({ isOpen, onClose, onAdd }) => {
  const [email, setEmail] = useState('');
  const [points, setPoints] = useState('0');
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoadingUsers(true);
      const token = localStorage.getItem('tt_auth_token') || localStorage.getItem('token');
      fetch(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const validUsers = data.filter((u: any) => u.role === 'CLIENTE' || u.role === 'PROFESSOR');
          // Sort alphabetically by name
          validUsers.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
          setUsers(validUsers);
        }
      })
      .catch(err => console.error(err))
      .finally(() => setLoadingUsers(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    onAdd({
      email,
      points: parseInt(points) || 0,
    });
    
    setEmail('');
    setPoints('0');
  };

  return (
    <AnimatePresence>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(4px)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: "400px",
            backgroundColor: "#1A112C",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "8px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "var(--primary-color)",
              padding: "20px 24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h2 style={{ color: "#2D1E4B", fontSize: "20px", fontWeight: 700, margin: 0 }}>
                Adicionar Jogador
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "none", color: "#2D1E4B", cursor: "pointer", display: "flex" }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                Selecione o Jogador *
              </label>
              <select 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{ 
                  width: '100%', 
                  padding: '12px 16px', 
                  borderRadius: '8px', 
                  border: '1px solid rgba(255,255,255,0.2)', 
                  background: 'rgba(255,255,255,0.05)', 
                  color: 'white', 
                  outline: 'none', 
                  boxSizing: 'border-box',
                  appearance: 'none'
                }}
              >
                <option value="" disabled style={{ color: 'black' }}>{loadingUsers ? 'Carregando...' : 'Selecione um usuário...'}</option>
                {users.map((u) => (
                  <option key={u.id} value={u.email} style={{ color: 'black' }}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                Pontuação Inicial
              </label>
              <input 
                type="number" 
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
              <button 
                type="button"
                onClick={onClose}
                style={{ padding: '10px 20px', background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button 
                type="submit"
                disabled={!email.trim() || loadingUsers}
                style={{ padding: '10px 20px', background: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', cursor: email.trim() ? 'pointer' : 'not-allowed', opacity: email.trim() ? 1 : 0.5 }}
              >
                <Plus size={20} />
                Adicionar
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
